import { useEffect, useRef, useState } from 'react';

import { useEditNoteMutation, useGetEditNoteStatusQuery } from '../store/api';

interface EditNoteRequest {
  note_id: string;
  organization_id?: string | null;
  text: string;
}

interface UseEditNoteWithContextState {
  isLoading: boolean;
  taskId: string | null;
  error: string | null;
  status: string | null;
  ready: boolean;
}

/**
 * Custom hook for editing notes with context using the workflow API.
 * Handles submitting the edit request and polling for completion.
 */
export const useEditNoteWithContext = () => {
  const [state, setState] = useState<UseEditNoteWithContextState>({
    isLoading: false,
    taskId: null,
    error: null,
    status: null,
    ready: false,
  });

  const [editNoteMutation] = useEditNoteMutation();
  const pollingIntervalRef = useRef<number | null>(null);
  const POLLING_INTERVAL = 2000; // Poll every 2 seconds
  const MAX_POLLING_ATTEMPTS = 60; // Max 2 minutes (60 * 2 seconds)
  const pollingAttemptsRef = useRef(0);

  // Use skip to control when to poll
  const { data: statusData } = useGetEditNoteStatusQuery(
    { taskId: state.taskId! },
    {
      skip: !state.taskId || state.ready,
      pollingInterval: state.taskId && !state.ready ? POLLING_INTERVAL : 0,
    },
  );

  // Handle status updates from polling
  useEffect(() => {
    if (!statusData || !state.taskId) return;

    pollingAttemptsRef.current++;

    if (statusData.ready) {
      // Task is complete
      if (statusData.successful) {
        // Check if the result contains an error message
        const result = statusData.result;
        const resultStatus = result?.result?.status;

        // Check if there's an error in the result status
        if (resultStatus && typeof resultStatus === 'string' && resultStatus.startsWith('error:')) {
          // Extract the error message
          const errorMessage = resultStatus.replace('error:', '').trim();
          setState({
            isLoading: false,
            taskId: null,
            error: errorMessage,
            status: statusData.status,
            ready: true,
          });
        } else {
          // Success - reload the page
          setState({
            isLoading: false,
            taskId: null,
            error: null,
            status: statusData.status,
            ready: true,
          });
          // Hard reload the page
          window.location.reload();
        }
      } else {
        // Task failed
        setState({
          isLoading: false,
          taskId: null,
          error: statusData.error || 'Edit request failed',
          status: statusData.status,
          ready: true,
        });
      }
    } else if (statusData.status === 'FAILURE' || statusData.status === 'failed') {
      // Task failed
      setState({
        isLoading: false,
        taskId: null,
        error: statusData.error || 'Edit request failed',
        status: statusData.status,
        ready: true,
      });
    } else if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
      // Max polling attempts reached
      setState({
        isLoading: false,
        taskId: null,
        error: 'Request timed out. Please try again.',
        status: 'TIMEOUT',
        ready: true,
      });
    }
  }, [statusData, state.taskId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const editNote = async (request: EditNoteRequest) => {
    try {
      setState({
        isLoading: true,
        taskId: null,
        error: null,
        status: null,
        ready: false,
      });

      pollingAttemptsRef.current = 0;

      // Wrap the request in the expected format for the API
      const result = await editNoteMutation({
        providerEditNoteRequest: request,
      }).unwrap();

      setState({
        isLoading: true,
        taskId: result.task_id,
        error: null,
        status: result.status,
        ready: false,
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.message || error?.message || 'Failed to submit edit request';
      setState({
        isLoading: false,
        taskId: null,
        error: errorMessage,
        status: 'ERROR',
        ready: true,
      });
    }
  };

  const clearError = (): void => {
    setState(prev => ({
      ...prev,
      error: null,
      taskId: null,
    }));
  };

  return {
    editNote,
    isLoading: state.isLoading,
    taskId: state.taskId,
    error: state.error,
    status: state.status,
    ready: state.ready,
    clearError,
  };
};
